import { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useHubspotExportScope from '@/composables/useHubspotExportScope';
import useSalesforceProxyGate from '@/composables/useSalesforceProxyGate';
import {
  DATA_TEMPLATES_CRMS,
  DataSourceFriendlyName,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { useDataSourcesStore, useFeedsStore, useSourcesStore } from '@/stores';
import { FeedIntegrationType } from '@/types/feeds';
import { DataSourceType } from '@/types/sources';

type AuthorizeParamsType = GetApiPayloadByUrl<
  '/v0.1/oauth/authorize',
  'post'
>['params'];

// We need access to it on both crm and gsheet card for the tags
const chosenCrm = ref<DataSourceType | null>(null);

export default function () {
  const router = useRouter();
  const { currentOrg, hasPermission } = useAuth();
  const dataSourcesStore = useDataSourcesStore();
  const { supportedDataSources } = storeToRefs(dataSourcesStore);
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const { optionalScopesFor } = useHubspotExportScope();
  const { isProxyUrlApplicable } = useSalesforceProxyGate();

  const { feeds, googleFeed } = storeToRefs(feedsStore);
  const { sources } = storeToRefs(sourcesStore);

  const viewOnly = computed(() => !hasPermission('manage:data-sources'));

  /* The organizations preferred CRM, set up when they log in */
  const preferredCrm = computed<DataSourceType>(() => {
    return currentOrg.value.preferred_crm || SALESFORCE_DATA_SOURCE_TYPE;
  });

  /* Choosing a CRM, when your preferred CRM is not available */
  onMounted(() => {
    if (preferredCrm.value) chosenCrm.value = preferredCrm.value;
  });
  const isChosenCrmSupported = computed(() =>
    Boolean(
      supportedDataSources.value.find((ds) => ds.type === chosenCrm.value)
        ?.is_supported,
    ),
  );

  const crmOptions = computed(() =>
    supportedDataSources.value
      .filter(({ type }) =>
        [...DATA_TEMPLATES_CRMS, SNOWFLAKE_DATA_SOURCE_TYPE].includes(type),
      )
      .map(({ type, friendly_name: name }) => ({
        display: name,
        value: type,
        svg: `${type}Icon`,
      }))
      .sort((a, b) =>
        a.display.toLowerCase() > b.display.toLowerCase() ? 1 : -1,
      ),
  );
  const chosenCrmFriendlyName = computed(() =>
    chosenCrm.value
      ? (DataSourceFriendlyName as Record<DataSourceType, string>)[
          chosenCrm.value as DataSourceType
        ]
      : 'Choose a CRM',
  );

  /* Code related to extra content in Connect Data Source sub-section */
  const feedsIntegrationTypeList = computed(() =>
    feeds.value.map((feed) => feed.integration.type),
  );
  const feedsIntegrationTypeWithoutGoogleSheetList = computed(() =>
    feedsIntegrationTypeList.value.filter(
      (type) => type !== GOOGLE_SHEETS_DATA_SOURCE_TYPE,
    ),
  );
  const hasConnectedGoogleSheets = computed(() => {
    const sourcesFeedIdList = computed(() =>
      sources.value.map((source) => source.feed_id),
    );
    return (
      sourcesFeedIdList.value.filter(
        (feedId) => feedId === googleFeed.value?.id,
      ).length > 0
    );
  });
  const connectedDataSourceList = computed(() => {
    if (hasConnectedGoogleSheets.value) return feedsIntegrationTypeList.value;
    // The gsheet dataSource in feedsIntegrationTypeList is listed as soon as the connection is active.
    // This is the google account connection, not the actual gsheets.
    // So if  no actual gsheets added, we don't want it to be listed here.
    return feedsIntegrationTypeWithoutGoogleSheetList.value;
  });

  const soleDataSourceConnectedFriendlyName = computed(() =>
    connectedDataSourceList.value.length === 1
      ? (DataSourceFriendlyName as Record<FeedIntegrationType, string>)[
          connectedDataSourceList.value[0] as FeedIntegrationType
        ]
      : null,
  );

  /* Connecting CRMs/Google */
  const isSandbox = ref(false);
  async function handleConnectCrm(type: FeedIntegrationType) {
    if (type === SNOWFLAKE_DATA_SOURCE_TYPE || isProxyUrlApplicable(type)) {
      await router.push({
        name: 'onboarding-data-sources-connect',
        params: { type },
      });
      return;
    }

    const payload: AuthorizeParamsType = {
      integration_type: type,
      settings_usecase:
        type === GOOGLE_SHEETS_DATA_SOURCE_TYPE ? 'recommended' : 'custom',
      ...optionalScopesFor(type),
    };

    if (isSandbox.value) payload.form_properties = { is_sandbox: true };
    const { data, error } = await crossbeamApi.POST('/v0.1/oauth/authorize', {
      body: { params: payload },
    });
    if (error) throw error;
    window.open(data.authorization_url, '_self');
  }

  /* Adding a CSV */
  const showAddDataModal = ref(false);
  async function handleAddCsv() {
    showAddDataModal.value = false;
    await router.push({
      name: 'onboarding_file_uploads',
      query: { cancelDestination: 'onboarding' },
    });
  }

  const isConnectCrmDisabled = computed(() =>
    connectedDataSourceList.value.includes(
      chosenCrm.value as FeedIntegrationType,
    ),
  );

  const isChosenCrmProxied = computed(() =>
    isProxyUrlApplicable(chosenCrm.value),
  );

  function handleChooseCrm({ value }: { value: DataSourceType }) {
    chosenCrm.value = value;
  }
  return {
    chosenCrm,
    chosenCrmFriendlyName,
    connectedDataSourceList,
    crmOptions,
    isChosenCrmSupported,
    isConnectCrmDisabled,
    isChosenCrmProxied,
    isSandbox,
    soleDataSourceConnectedFriendlyName,
    viewOnly,
    handleAddCsv,
    handleChooseCrm,
    handleConnectCrm,
  };
}
